import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import {
  Badge, Button, Card, CardBody, CardHeader, Col, Collapse, Progress, Row, Spinner,
} from 'reactstrap';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import { get } from '../../helpers/api_helper';
import { Can } from '../../utilities/Can';
import WarehouseMapCanvas from '../../components/Warehouse/WarehouseMapCanvas';

function PutAwayRoute(props) {
  const { id } = useParams();
  const { t } = props;
  const [loading, setLoading] = useState(true);
  const [route, setRoute] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);

  useEffect(() => {
    const fetchRoute = async () => {
      try {
        setRoute(await get(`/api/warehouse/putaway/route/${id}`));
      } catch (error) {
        toastr.error(error.message, t('Could not load put-away route.'));
      } finally {
        setLoading(false);
      }
    };

    fetchRoute();
  }, [id, t]);

  const stops = route?.stops || [];
  const completedStops = stops.filter((stop) => stop.completed_at !== null).length;
  const totalQty = stops.reduce((sum, stop) => sum + Number(stop.qty || 0), 0);
  const completedQty = stops
    .filter((stop) => stop.completed_at !== null && !stop.note?.startsWith('Missing:'))
    .reduce((sum, stop) => sum + Number(stop.qty || 0), 0);
  const progress = stops.length === 0 ? 0 : (completedStops / stops.length) * 100;
  const isFinished = route?.completed_at !== null && route?.completed_at !== undefined;
  const locationLabels = useMemo(() => stops
    .map((stop) => stop.location?.label)
    .filter(Boolean), [stops]);

  return (
    <div className="page-content pickroute-page">
      <div className="container-fluid">
        <Can i="see" this="putaway">
          <Row>
            <Col md="3">
              <Card className="pickroute-page__panel-card">
                <CardHeader className="text-uppercase bg-transparent border-bottom">{t('Details')}</CardHeader>
                <CardBody>
                  {loading ? (
                    <div className="text-center py-4"><Spinner color="primary" /></div>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <div className="text-uppercase text-muted small">{t('Put-Away Route ID')}</div>
                          <div className="fs-4 fw-bold">{id}</div>
                        </div>
                        <Badge pill color={isFinished ? 'success' : 'warning'} className={isFinished ? '' : 'text-dark'}>
                          {isFinished ? t('Finished') : t('In Progress')}
                        </Badge>
                      </div>

                      <div className="mb-3">
                        <div className="d-flex justify-content-between small mb-1">
                          <span>{t('Progress')}</span>
                          <strong>{`${completedStops}/${stops.length} ${t('Stops')}`}</strong>
                        </div>
                        <Progress striped={!isFinished} animated={!isFinished} color={isFinished ? 'success' : 'primary'} value={progress} />
                      </div>

                      <dl className="row mb-0">
                        <dt className="col-5">{t('Created By')}</dt>
                        <dd className="col-7">{route?.created_by || '-'}</dd>
                        <dt className="col-5">{t('Items')}</dt>
                        <dd className="col-7">{`${completedQty}/${totalQty}`}</dd>
                        <dt className="col-5">{t('Created')}</dt>
                        <dd className="col-7">{route?.created_at ? moment(route.created_at).format('L LT') : '-'}</dd>
                        <dt className="col-5">{t('Finished')}</dt>
                        <dd className="col-7">{route?.completed_at ? moment(route.completed_at).format('L LT') : '-'}</dd>
                      </dl>

                      {route?.note && (
                        <div className="border-top mt-2 pt-2">
                          <div className="text-uppercase text-muted small">{t('Notes')}</div>
                          <div>{route.note}</div>
                        </div>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col md="9">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Link to="/put-away/routes" className="btn btn-sm btn-soft-secondary">
                  <span className="mdi mdi-arrow-left" />
                  {' '}
                  {t('Put-Away Routes')}
                </Link>
                <Button color="link" className="p-0 text-decoration-none" onClick={() => setMapOpen((open) => !open)}>
                  {mapOpen ? t('Hide Map') : t('Show Map')}
                </Button>
              </div>

              <Collapse isOpen={mapOpen}>
                <WarehouseMapCanvas
                  layoutModeType={props.layoutModeType}
                  locations={locationLabels}
                />
              </Collapse>

              <Card className="pickroute-page__panel-card">
                <CardHeader className="text-uppercase bg-transparent border-bottom">{t('Route Stops')}</CardHeader>
                <CardBody>
                  {loading && <div className="text-center py-4"><Spinner color="primary" /></div>}
                  {!loading && stops.length === 0 && <p className="text-muted mb-0">{t('No route stops found.')}</p>}
                  <Row>
                    {stops.map((stop, index) => {
                      const isMissing = stop.note?.startsWith('Missing:');
                      let statusColor = stop.completed_at ? 'success' : 'warning';
                      let statusLabel = stop.completed_at ? t('Completed') : t('Pending');
                      if (isMissing) {
                        statusColor = 'danger';
                        statusLabel = t('Missing');
                      }
                      const item = stop.inventory || stop.kit;
                      const itemUrl = stop.inventory ? `/inventory/${stop.inventory.id}` : `/kits/${stop.kit?.id}`;
                      const thumbnailUrl = stop.inventory
                        ? `/api/inventory/thumbnail/${stop.inventory.id}`
                        : `/api/inventory/kit/thumbnail/${stop.kit?.id}`;

                      return (
                        <Col lg="6" className="mb-3" key={stop.id}>
                          <Card className="h-100 border shadow-sm">
                            <CardBody>
                              <div className="d-flex justify-content-between align-items-start mb-3">
                                <div>
                                  <span className="text-muted small">{`${t('Stop')} ${index + 1}`}</span>
                                  <h5 className="mb-0">
                                    {stop.location?.label || t('No destination')}
                                    {stop.location && stop.had_stock_at_generation === false && (
                                      <span title={t('New SKU at this location when route was generated')} aria-label={t('New SKU at this location when route was generated')}>{' ★'}</span>
                                    )}
                                  </h5>
                                </div>
                                <Badge color={statusColor} className={stop.completed_at ? '' : 'text-dark'}>
                                  {statusLabel}
                                </Badge>
                              </div>

                              <div className="d-flex gap-3 align-items-center">
                                {item && (
                                  <div className="d-flex align-items-center justify-content-center bg-light rounded" style={{ width: '84px', minWidth: '84px', height: '84px' }}>
                                    <img src={thumbnailUrl} style={{ maxWidth: '72px', maxHeight: '72px' }} alt={item.ship_desc || `KIT-${item.stock_id}`} />
                                  </div>
                                )}
                                <div>
                                  <div className="fw-bold">{stop.stock_id}</div>
                                  {item && <Link to={itemUrl}>{item.ship_desc || `KIT-${item.stock_id}`}</Link>}
                                  <div className="small mt-2">
                                    <strong>
                                      {t('Qty')}
                                      :
                                    </strong>
                                    {' '}
                                    {stop.qty}
                                    {' · '}
                                    <strong>
                                      {t('Lot')}
                                      :
                                    </strong>
                                    {' '}
                                    {stop.lot || '-'}
                                  </div>
                                  {stop.area_name && <div className="small text-muted">{stop.area_name}</div>}
                                </div>
                              </div>

                              {stop.completed_at && !isMissing && (
                                <div className="small text-success border-top mt-3 pt-2">
                                  <span className="mdi mdi-check-circle" />
                                  {' '}
                                  {`${t('Completed')} ${moment(stop.completed_at).format('L LT')}`}
                                </div>
                              )}
                            </CardBody>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Can>
        <Can not i="see" this="putaway"><p>{t('Unauthorized')}</p></Can>
      </div>
    </div>
  );
}

PutAwayRoute.propTypes = {
  layoutModeType: PropTypes.string,
  t: PropTypes.func,
};

PutAwayRoute.defaultProps = {
  layoutModeType: 'light',
  t: (value) => value,
};

export default connect((state) => ({
  layoutModeType: state.Layout.layoutModeType,
}), {})(withTranslation()(PutAwayRoute));
